import { apiClient } from './client';
import type {
  WinchStartPointsResponse,
  WinchStartPointResponse,
  ActiveStartPointResponse,
  CreateWinchStartPointDTO,
  UpdateWinchStartPointDTO,
  WinchPriceCalculationResponse,
} from '@/types';

/**
 * Winch Start Points API Client
 * Provides methods for managing winch service base locations
 */
export const winchStartPointsAPI = {
  /**
   * Get all winch start points
   */
  getAll: (includeInactive = false) =>
    apiClient.get<WinchStartPointsResponse>('/winch-start-points', {
      params: { includeInactive },
    }),

  /**
   * Get the currently active start point
   */
  getActive: () =>
    apiClient.get<ActiveStartPointResponse>('/winch-start-points/active'),

  /**
   * Get a specific start point by ID
   */
  getById: (id: number) =>
    apiClient.get<WinchStartPointResponse>(`/winch-start-points/${id}`),

  /**
   * Create a new start point (Manager only)
   */
  create: (data: CreateWinchStartPointDTO) =>
    apiClient.post<WinchStartPointResponse>('/winch-start-points', data),

  /**
   * Update an existing start point (Manager only)
   */
  update: (id: number, data: UpdateWinchStartPointDTO) =>
    apiClient.put<WinchStartPointResponse>(`/winch-start-points/${id}`, data),

  /**
   * Activate a start point (deactivates all others) (Manager only)
   */
  activate: (id: number) =>
    apiClient.put<WinchStartPointResponse>(`/winch-start-points/${id}/activate`),

  /**
   * Delete a start point (Manager only)
   * Note: Cannot delete the active start point
   */
  delete: (id: number) =>
    apiClient.delete(`/winch-start-points/${id}`),

  /**
   * Calculate winch price based on regions with distance breakdown
   */
  calculatePriceByRegions: (data: {
    serviceId: number;
    pickupRegionId: number;
    destinationRegionId: number;
  }) =>
    apiClient.post<WinchPriceCalculationResponse>(
      '/winch-pricing/calculate-by-regions',
      data
    ),

  /**
   * Calculate winch price using custom coordinates
   */
  calculatePriceByCoordinates: (data: {
    serviceId: number;
    pickupLatitude: number;
    pickupLongitude: number;
    destinationLatitude: number;
    destinationLongitude: number;
  }) =>
    apiClient.post<WinchPriceCalculationResponse>(
      '/winch-pricing/calculate-by-coordinates',
      data
    ),
};
